// Purpose of This File: Enhancing Product Listing Page (PLP) UI
// This file was specifically created to enhance the User Interface (UI) of the Product Listing Page (PLP).
// The existing product details were minimal, so this new file aligns with the full - fledged UI design for product representation.

import React, { FC, useEffect, useMemo, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import Image, { ImageProps } from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { desktop, mediumDesktop, tablet } from 'helpers/utils/screensizes';
import useTrack from '../../../product-tile/useTrack';
// import { CenterImageCropSwatch } from 'components/commercetools-ui/organisms/youMayAlsoLike/tail';
import { CheckedSwatch } from 'components/commercetools-ui/organisms/youMayAlsoLike/tail';
import Prices from '../../../product-tile/prices';
// import Link from 'components/commercetools-ui/atoms/link';
import Link from 'next/link';
import Price from '../Algolia-search-Page/price';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import aa from 'search-insights';
import useLocale from 'helpers/hooks/useLocale';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { useAccount } from 'frontastic';
import Loading from 'components/commercetools-ui/atoms/loading';
import PriceMap from '../AlgoliaPrice/priceMap';
import { useCookies } from 'react-cookie';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import { Swatch } from 'helpers/algolia/map-product';

import { useInstantSearch } from 'react-instantsearch-hooks';
import { getHexCode } from 'helpers/utils/getHexcode';
import { CartDiscount } from '../../../product-details/types';
import { CLICKED_OBJECT_ID, SEARCH_QUERY_ID } from 'helpers/constants/algolia';
import { mobile } from 'helpers/utils/screensizes';
import { TrustBoxCss } from 'frontastic/tastics/products/details';
import axios from 'axios';
import Zerostar from 'public/images/Trustpilot_ratings_0star_grey-RGB.svg';
import OneStar from 'public/images/Trustpilot_ratings_1star-RGB.svg';
import OneAndHalf from 'public/images/Trustpilot_ratings_1halfstar-RGB.svg';
import TwoStar from 'public/images/Trustpilot_ratings_2star-RGB.svg';
import TwoAndHalf from 'public/images/Trustpilot_ratings_2halfstar-RGB.svg';
import ThreeStar from 'public/images/Trustpilot_ratings_3star-RGB.svg';
import ThreeAndHalf from 'public/images/Trustpilot_ratings_3halfstar-RGB.svg';
import FourStar from 'public/images/Trustpilot_ratings_4star-RGB.svg';
import FourAndHalf from 'public/images/Trustpilot_ratings_4halfstar-RGB.svg';
import FiveStar from 'public/images/FiveStar.svg';
import { fetchTrustPilotData } from 'helpers/utils/trustPilotReview';
import { P2Badge } from '../../types';
import { StringHelpers } from 'helpers/stringHelpers';

interface ProductListTileProps {
  product: Product;
  productLogo?: ImageProps;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  onCompareChange?: (product: Product, checked: boolean) => void;
  isCompared?: boolean;
  handleCompareChange: (product: Product) => void;
  setOpen: (b: boolean) => void;
  compareProduct: any;
  p1BadgeData?: any;
  p2BadgeData?: P2Badge[];
  slug?: string;
  queryId?: string;
  position?: number;
  trustBoxCss?: TrustBoxCss;
}

const ProductListTile: FC<ProductListTileProps> = ({
  handleCompareChange,
  product,
  productLogo,
  onClick,
  isSearchResult = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
  onCompareChange,
  isCompared = false,
  setOpen,
  compareProduct,
  p1BadgeData,
  p2BadgeData,
  slug,
  queryId,
  position,
  trustBoxCss,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [isInDesktopRange] = useMediaQuery([desktop, mediumDesktop - 1]);
  const [isMobile] = useMediaQuery(mobile);
  const { ref } = useTrack({ product });
  const { _url } = product;
  const urlArray = _url?.split('/') ?? [];
  const catUrl = slug?.split('_')?.[0];
  const [imageError, setImageError] = useState(false);
  const pdpUrl = `/${catUrl}/${urlArray[urlArray?.length - 1]}`;
  const variantWithDiscount = useVariantWithDiscount(product?.variants) as Variant;
  const [imageHovered, setImageHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [hasReviews, setHasReviews] = useState(false);
  const [trustPilotSkuId, setTrustPilotSkuId] = useState('');
  const [trustPilotRes, setTrustPilotRes] = useState<any>();

  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  //   const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);
  //   const [hoveredVariant, setHoveredVariant] = useState<Variant | null>(null); // Initialize with null
  //   const [selectedColour, setSelectedColour] = useState<string | null>(
  //     variantWithDiscount ? variantWithDiscount?.attributes?.colour : product?.variants[0]?.attributes?.colour || null,
  //   );
  const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);
  const [selectedSwatch, setSelectedSwatch] = useState(() =>
    product?.swatches ? product?.swatches?.find((swatch) => swatch?.variantCode === product?.variantCode) : null,
  );
  const [hoveredSwatch, setHoveredSwatch] = useState<Swatch | null>(null); // Initialize with null
  const [selectedColour, setSelectedColour] = useState<string | null>(
    product?.swatches ? product?.swatches[0]?.colourHexCode : '',
  );
  const { loggedIn, result } = useAccount();
  const { locale, getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';
  const isProductInCompare = compareProduct?.some((p: any) => p?.productKey === product?.productKey);
  // const [isLoading, setIsLoading] = useState(false);
  const [price, setPrice] = useState<any>(null);
  const [mrrpPrice, setMrrpPrice] = useState<any>(null);
  const [wasPrice, setWasPrice] = useState<any>(null);
  const [StarImage, setStarImage] = useState<React.FC | null>(null);
  const [starsAverage, setStarsAverage] = useState<number | null>(null);
  const [numberOfReviews, setNumberOfReviews] = useState<number | null>(null);
  const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/?.test(navigator?.userAgent);
  const targetVariant = product?.variants?.find((attributes) => attributes?.sku === selectedSwatch?.sku);
  const cartDiscount = (targetVariant?.attributes?.cartDiscount &&
    JSON.parse(targetVariant?.attributes?.cartDiscount as string)) as CartDiscount[];
  const now = new Date();

  const isNew = useMemo(() => {
    const dateStr = product?.firstReceiptDate;
    if (!dateStr) return false;
    const firstReceipt = new Date(dateStr as string);
    if (isNaN(firstReceipt.getTime())) return false;
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
    return firstReceipt >= twoMonthsAgo && firstReceipt <= now;
  }, []);
  const isSale = useMemo(() => {
    const categories = (product?.categories as any)?.['en-GB']?.lvl0;
    if (!Array.isArray(categories)) return false;

    return categories.some((category: string) => category?.toLowerCase?.().includes('sale'));
  }, []);
  const isCustomFitProduct = product?.customFit === 'custom' || false;
  const activeP1Badges = p1BadgeData?.filter((badgemap: any) =>
    badgemap?.variantCodes?.includes(selectedSwatch?.variantCode),
  );
  const activeP2Badges = [
    isNew ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'new') : null,
    isSale ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'sale') : null,
    isCustomFitProduct ? p2BadgeData?.find((badge: P2Badge) => badge?.id === 'customFit') : null,
  ].filter(Boolean);
  const allBadges = [...(Array.isArray(activeP1Badges) ? activeP1Badges : []), ...activeP2Badges];
  const getRanking = (badge: any) => Number(badge?.ranking ?? badge?.ranking ?? 99);
  const sortedBadges = allBadges.sort((a, b) => getRanking(a) - getRanking(b));

  const imageBadges = sortedBadges.filter(
    (badge) => (isMobile ? badge.visibility?.mobile?.container : badge.visibility?.desktop?.container) === 'image',
  );

  const menuBadges = sortedBadges.filter(
    (badge) => (isMobile ? badge.visibility?.mobile?.container : badge.visibility?.desktop?.container) === 'menu',
  );
  const visibleImageBadges = imageBadges.filter((badge) => {
    const { isHidden, validFrom, validTo } = badge.conditions || {};
    if (isHidden) return false;
    const from = validFrom ? new Date(validFrom) : null;
    const to = validTo ? new Date(validTo) : null;
    return (!from || from <= now) && (!to || to >= now);
  });
  const groupedImageBadges = visibleImageBadges.reduce((acc, badge) => {
    const position = isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position;
    if (!acc[position]) acc[position] = [];
    acc[position].push(badge);
    return acc;
  }, {} as Record<string, any[]>);
  const visibleMenuBadges = menuBadges.filter((badge) => {
    const { isHidden, validFrom, validTo } = badge.conditions || {};
    if (isHidden) return false;
    const from = validFrom ? new Date(validFrom) : null;
    const to = validTo ? new Date(validTo) : null;
    return (!from || from <= now) && (!to || to >= now);
  });
  const getPositionClass = (position: string) => {
    switch (position) {
      case 'top-right':
        return 'top-0 right-0 items-end';
      case 'bottom-center':
        return 'bottom-0 left-1/2 transform -translate-x-1/2 items-center';
      case 'left':
        return 'top-1/2 left-0 transform -translate-y-1/2';
      case 'center':
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
      case 'right':
        return 'top-1/2 right-0 transform -translate-y-1/2';
      default:
        return 'top-0 right-0';
    }
  };

  useEffect(() => {
    if (hoveredSwatch) {
      const filteredPrice = product?.standalonePrice?.find((price: any) => price?.variantSku === hoveredSwatch?.sku);
      const isGBP = cookies?.locale_selection?.currency === 'GBP';

      // Set the states based on the selected currency
      setPrice(isGBP ? filteredPrice?.priceGB : filteredPrice?.priceEU);
      setMrrpPrice(isGBP ? filteredPrice?.mrrpPriceGB : filteredPrice?.mrrpPriceEU);
      setWasPrice(isGBP ? filteredPrice?.wasPriceGB : filteredPrice?.wasPriceEU);
    }
  }, [hoveredSwatch]);
  const mrrpPounds = mrrpPrice / Math.pow(10, 2);
  const pricePounds = price / Math.pow(10, 2);

  // const handleNavigation = (link: string) => {
  //   // window.location.href = link;
  //   // setIsLoading(true);
  //   // const timeoutId = setTimeout(() => {
  //   //   setIsLoading(false);
  //   // }, 1000);

  //   // return () => clearTimeout(timeoutId);
  //   // setIsLoading(true);
  //   requestAnimationFrame(() => {
  //     window.location.href = link;
  //   });
  // };
  // const buttonIsVisible = useMemo(
  //   () => (imageHovered || buttonHovered) && isDesktopSize,
  //   [imageHovered, buttonHovered, isDesktopSize],
  // );

  // const handleColourSelect = (variant: Variant, objectId: string) => {
  //     handleProductSwatchClick(locale, [objectId]);
  //     setSelectedColour(variant?.attributes?.colour);
  //     setSelectedVariant(variant);
  // };

  const handleColourSelect = (swatch: Swatch, objectId: string) => {
    handleProductSwatchClick(locale, [objectId]);
    setSelectedColour(swatch?.colourHexCode);
    setSelectedSwatch(swatch);
  };

  // const uniqueColorVariants = useMemo(() => {
  //     const seenColours = new Set();
  //     return product.variants.filter((variant: Variant) => {
  //         const colour = variant?.attributes?.colour;
  //         if (!colour || seenColours.has(colour)) {
  //             return false;
  //         }
  //         seenColours.add(colour);
  //         return true;
  //     });
  // }, [product.variants]);

  // const uniqueColorVariants = useMemo(() => {
  //     const seenColours = new Set();
  //     return product?.swatches?.filter((swatch: Swatch) => {
  //         const colour = swatch?.variantCode;
  //         if (!colour || seenColours.has(colour)) {
  //             return false;
  //         }
  //         seenColours.add(colour);
  //         return true;
  //     });
  // }, [product.swatches]);

  const uniqueColorVariants: Swatch[] = useMemo(() => {
    return product?.swatches?.filter((swatch) => swatch?.colour) ?? [];
  }, [product?.swatches]);

  const sortedColorVariants = useMemo(() => {
    if (!uniqueColorVariants || !selectedSwatch) return uniqueColorVariants ?? [];
    const selectedSwatchIndex = uniqueColorVariants?.findIndex(
      (swatch) => swatch?.variantCode === selectedSwatch?.variantCode,
    );
    if (selectedSwatchIndex === -1) return uniqueColorVariants;
    return [
      uniqueColorVariants?.[selectedSwatchIndex],
      ...uniqueColorVariants?.slice(0, selectedSwatchIndex),
      ...uniqueColorVariants?.slice(selectedSwatchIndex + 1),
    ];
  }, []);

  // const sortedSwatches = useMemo(() => {
  //     if (!uniqueColorVariants || !selectedSwatch?.colourHexCode) return uniqueColorVariants;

  //     return [
  //         ...uniqueColorVariants?.filter((swatch) => swatch?.colourHexCode === selectedSwatch?.colourHexCode),
  //         ...uniqueColorVariants?.filter((swatch) => swatch?.colourHexCode !== selectedSwatch?.colourHexCode),
  //     ];
  // }, [uniqueColorVariants, selectedSwatch]);

  const badgeStyles = {
    BLACKNOVEMBER: 'bg-black text-white h-[22px] w-[95px] md:h-[30px] md:w-[160px]',
    SALE: 'bg-[#E30616] text-white h-[22px] w-[28px] md:h-[30px] md:w-[67px]',
    NEW: 'bg-[#137E57] text-white h-[22px] w-[38px] md:h-[30px] md:w-[68px]',
    MEGADEALS: 'bg-gradient-to-r from-[#272C59] to-[#BD2F48] text-white h-[22px] w-[65px] md:h-[30px] md:w-[112px]',
    CHIRSTMAS: 'bg-[#137E57] text-white h-[22px] w-[65px] md:h-[30px] md:w-[120px]',
    TOPGIFT: 'bg-[#D9BE65] text-black h-[20px] w-[53px] md:h-[30px] md:w-[93px]',
    BANKHOLIDAY: 'bg-[#BD2F48] text-white h-[22px] w-[79px] md:h-[30px] md:w-[134px]',
    BANKHOLIDAYDEAL: 'bg-[#BD2F48] text-white h-[22px] w-[103px] md:h-[30px] md:w-[173px]',
    FREEACCESSORYPACK: 'bg-[#1E1E1E] text-white h-[22px] w-[115px] md:h-[30px] md:w-[193px]',
  };
  const userId = loggedIn ? result?.data?.data?.account?.accountId : localStorage.getItem('__anon_id');
  const isGBP = cookies?.locale_selection?.currency === 'GBP';
  const handleProductClick =
    (locale: string, productId: string | undefined) => (event: React.MouseEvent<HTMLDivElement>) => {
      // setIsLoading(true);
      const analytics = new AlgoliaAnalytics(locale);
      if (isSearchResult && productId && queryId && position) {
        analytics?.handleClickAfterSearch([productId], userId, queryId, [position]);
      } else {
        analytics?.handleProductClick(productId, queryId, position);
      }
      if (productId) sessionStorage?.setItem(CLICKED_OBJECT_ID, productId);
      if (queryId) sessionStorage?.setItem(SEARCH_QUERY_ID, queryId);
    };

  const getDescriptionFromLocale = (
    description: Record<string, string>,
    locale: string | undefined,
  ): string | undefined => {
    if (!locale) return undefined;

    const normalizedLocale = locale.replace('_', '-').toLowerCase();
    return description?.[normalizedLocale];
  };
  const handleProductSwatchClick = (locale: string, objectId: string[]) => {
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleColourSwatchesClickedInPLP(objectId, userId);
  };
  //Newly Updated code for calculating the discount percentage
  const discountPercentage = useMemo(() => {
    if (mrrpPrice <= 0 || price <= 0 || mrrpPrice === price || mrrpPrice < price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrpPrice - price) / mrrpPrice) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [mrrpPrice, price]);

  const mrrpPoundsDiscount = useMemo(() => {
    if (!mrrpPounds || !pricePounds || mrrpPounds <= 0 || pricePounds <= 0) {
      return null;
    }
    return mrrpPounds - pricePounds;
  }, [mrrpPounds, pricePounds]);

  useEffect(() => {
    if (!product?.standalonePrice || product?.standalonePrice?.length === 0) {
      // Reset the states if no standalonePrice is available
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
      return;
    }

    // const filteredPrice = product?.standalonePrice?.find((price: any) => price?.variantSku === selectedVariant?.sku);
    const filteredPrice = product?.standalonePrice?.find((price: any) => price?.variantSku === selectedSwatch?.sku);
    if (filteredPrice) {
      // Set the states based on the selected currency
      setPrice(isGBP ? filteredPrice?.priceGB : filteredPrice?.priceEU);
      setMrrpPrice(isGBP ? filteredPrice?.mrrpPriceGB : filteredPrice?.mrrpPriceEU);
      setWasPrice(isGBP ? filteredPrice?.wasPriceGB : filteredPrice?.wasPriceEU);
    } else {
      // Reset the states if no price is found
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
    }
    // }, [product?.standalonePrice, selectedVariant?.sku, cookies?.locale_selection?.currency]);
  }, [product?.standalonePrice, selectedSwatch?.sku, cookies?.locale_selection?.currency]);

  // const cartDiscount: string[] = ((product?.offer as { id: string; description: string }[]) || [])
  //   ?.map((offer) => offer?.description)
  //   ?.filter((desc: string) => desc !== '');

  const productColour = selectedSwatch?.colour || product?.colourDisplay || '';
  const productLink = isCustomFitProduct
    ? product?._url ?? ''
    : `${product?._url ?? ''}?variantCode=${selectedSwatch?.variantCode ?? ''}`;
  const isPLP = true;
  useEffect(() => {
    const fetchData = async () => {
      const result = await fetchTrustPilotData(product, undefined, isPLP);
      console.log(result);
      if (result) {
        setTrustPilotRes(result?.raw);
        setStarsAverage(result?.starsAverage);
        setNumberOfReviews(result?.numberOfReviews);
        setStarImage(() => result?.StarImageComponent);
      }
    };

    fetchData();
  }, [product]);

  return (
    <>
      {/* {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : ( */}
      <div
        ref={ref}
        className="flex h-full flex-col justify-between border p-8  sm:p-0 md:border-0"
        // onClick={() => {
        //   handleProductClick(locale, product?.objectId);
        //   handleNavigation(
        //     `${isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}`,
        //   );
        // }}
      >
        <SmartLink
          // href={isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}
          // href={
          //     isCustomFitProduct
          //         ? product?._url ?? ''
          //         : `${product?._url ?? ''}?variant=${selectedVariant?.sku ?? product?.standalonePrice?.[0]?.variantSku ?? ''
          //         }`
          // }
          href={productLink}
          callback={handleProductClick(locale, product?.objectId)}
          // onClick={(e) => {
          //   // Detect "open in new tab" or non-left-clicks
          //   if (
          //     e.metaKey || // ⌘ Cmd on Mac
          //     e.ctrlKey || // Ctrl on Windows/Linux
          //     e.shiftKey || // Shift+Click
          //     e.button !== 0 // Middle click (button 1), right click (button 2)
          //   ) {
          //     // Let the browser handle it normally
          //     return;
          //   }

          //   // Prevent default only for left-click navigation
          //   e.preventDefault();
          //   handleProductClick(locale, product?.objectId);
          //   handleNavigation(
          //     `${
          //       isCustomFitProduct
          //         ? product?._url
          //         : `${product?._url}?variant=${
          //             selectedVariant?.sku ? selectedVariant?.sku : product?.standalonePrice?.[0]?.variantSku
          //           }`
          //     }`,
          //     // product?._url ?? '',
          //   );
          // }}
        >
          <div className="relative">
            {/* <Link
              link={`${isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}`}
            > */}
            <div
              className="relative w-full"
              onMouseEnter={() => setImageHovered(true)}
              onMouseLeave={() => setImageHovered(false)}
            >
              <div
                className="relative w-full"
                onMouseEnter={() => setImageHovered(true)}
                onMouseLeave={() => setImageHovered(false)}
              >
                <div className="relative  bg-white">
                  <div className="relative block " style={{ paddingBottom: '122%' }}>
                    <Image
                      // src={hoveredVariant?.images?.[0] || selectedVariant.images?.[0] || productLogo?.media?.file}
                      src={
                        hoveredSwatch?.images?.[0] ||
                        (selectedSwatch?.images ? selectedSwatch?.images?.[0] : null) ||
                        productLogo?.media?.file ||
                        (product.colourImages && product?.colourImages[0])
                      }
                      suffix="medium"
                      alt={product.name ?? ''}
                      style={{ objectFit: 'contain', objectPosition: 'center' }}
                      className="w-full rounded-sm group-hover:opacity-75"
                      fill
                    />
                    {typeof discountPercentage === 'number' &&
                      discountPercentage > 0 &&
                      typeof mrrpPoundsDiscount === 'number' &&
                      mrrpPoundsDiscount && (
                        <div className="absolute left-0 top-0 z-10 flex h-[20px] min-w-[39px] items-center justify-center gap-8 self-stretch rounded-[2px] border border-[#686868] bg-white px-6 py-8 sm:left-2 sm:top-2 sm:h-[22px] sm:min-w-[46px]">
                          {/* <div className=" h-[20px] min-w-[39px] lg:h-[22px] lg:min-w-[46px]"> */}
                          <p
                            className={`text-center font-body text-11 font-bold not-italic
                 text-[#686868] sm:text-14 ${
                   isIOS ? 'mb-0 pb-0 pt-4 leading-[30px] sm:leading-[30px]' : 'leading-[12.1px] sm:leading-[15.4px]'
                 }`}
                          >
                            {mrrpPounds < 200
                              ? `-${discountPercentage}%`
                              : `-${cookies?.locale_selection?.currency === 'GBP' ? '£' : '€'}${Math.floor(
                                  mrrpPoundsDiscount,
                                )}`}
                          </p>
                          {/* </div> */}
                        </div>
                      )}
                    {/* {badgeData?.campaign_Items?.length > 0 && (
                      <div className="absolute right-0 top-0 flex flex-col items-end gap-y-3">
                        {badgeData?.campaign_Items?.map((item: any) =>
                          (badgeStyles as any)[item?.replace(/\s+/g, '')?.toUpperCase()] ? (
                            <div
                              key={item}
                              className={`flex items-center justify-center text-11 md:text-18 ${
                                (badgeStyles as any)[item?.replace(/\s+/g, '')?.toUpperCase()]
                              }`}
                            >
                              <p className="text-center">{item}</p>
                            </div>
                          ) : null,
                        )}
                      </div>
                    )} */}
                    {Object.entries(groupedImageBadges).map(([position, badges]) => (
                      <div
                        key={position}
                        className={`absolute flex w-fit flex-col gap-y-4 ${getPositionClass(position)}`}
                      >
                        {(badges as any[]).slice(0, 2)?.map((item: any, index: number) => {
                          const styles = item?.styles;
                          const desktopPosition = item?.visibility?.desktop?.position;
                          const mobilePosition = item?.visibility?.mobile?.position;
                          const iconPosition = item?.icon?.position || 'left';

                          const inlineStyles = {
                            backgroundColor: styles?.backgroundColor,
                            color: styles?.textColor,
                            fontSize: `${!isMobile ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                            fontWeight: styles?.fontWeight,
                            padding: !isMobile ? styles?.paddingMobile : styles?.paddingDesktop,
                            minHeight: `${!isMobile ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                            maxHeight: `${!isMobile ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: `${!isMobile ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                            borderWidth: `${styles?.borderThickness || 0}px`,
                            borderColor: styles?.borderColor,
                          };
                          const iconElement = item?.icon?.asset && (
                            <img
                              src={`https://${item?.icon?.asset?.[selectedLocale]?.endpoint}.a.bigcontent.io/v1/static/${item?.icon?.asset?.[selectedLocale]?.name}`}
                              alt=""
                              width={item?.icon?.sizePx}
                              height={item?.icon?.sizePx}
                              style={{
                                marginRight: iconPosition === 'left' ? item?.icon?.spacingPx : 0,
                                marginLeft: iconPosition === 'right' ? item?.icon?.spacingPx : 0,
                              }}
                            />
                          );

                          return (
                            //    <div
                            //   key={item?.label || index}
                            //   className={`absolute flex w-max max-w-full flex-col gap-y-3 ${getPositionClass(
                            //     isDesktopSize ? desktopPosition : mobilePosition,
                            //   )}`}
                            // >
                            <div key={item?.label || index} style={inlineStyles} className="w-fit rounded-sm">
                              {iconPosition === 'left' && iconElement}
                              <p className="text-center">{item?.label?.[selectedLocale]}</p>
                              {iconPosition === 'right' && iconElement}
                            </div>
                            // {/* </div> */}
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* </Link> */}
            </div>

            {/* <Link link={`${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}> */}
            <div className="text-center">
              {((product?.offer as { id: string; description: string }[] | undefined)?.length ?? 0) > 0 && (
                <div className="mb-4 flex w-full items-center justify-center">
                  <div className="relative flex min-h-[40px] flex-col items-center justify-center whitespace-normal break-words rounded-sm bg-ag_color-custom_green px-8 pb-[4.5px] pt-[8.2px] font-body text-[14px] capitalize leading-[14px] md:px-8  md:py-6 md:text-[16px] md:leading-[18px] lg:px-10 lg:py-6 lg:text-[18px] ">
                    {(product?.offer as Array<{ description: string }>)?.[0]?.description
                      ?.toLowerCase()
                      ?.split(',')
                      ?.map((part, idx, arr) =>
                        idx < arr?.length - 1 ? (
                          <span key={idx}>
                            {part?.trim()}
                            <br />
                          </span>
                        ) : (
                          <span key={idx}>{part?.trim()}</span>
                        ),
                      )}
                  </div>
                </div>
              )}
              {visibleMenuBadges.length > 0 && (
                <div className="mb-12 flex flex-col gap-y-3">
                  {visibleMenuBadges?.slice(0, 2)?.map((badge, idx) => {
                    const iconPosition = badge?.icon?.position || 'left';
                    const label = badge?.label?.[selectedLocale];
                    const containerPosition =
                      (isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position) ||
                      'center';
                    const styles = badge?.styles;
                    const selfClass =
                      containerPosition === 'center'
                        ? 'self-center'
                        : containerPosition === 'right'
                        ? 'self-end'
                        : 'self-start';

                    const iconElement = badge?.icon?.asset && (
                      <img
                        src={`https://${badge?.icon?.asset?.[selectedLocale]?.endpoint}.a.bigcontent.io/v1/static/${badge?.icon?.asset?.[selectedLocale]?.name}`}
                        alt=""
                        width={badge?.icon?.sizePx}
                        height={badge?.icon?.sizePx}
                        style={{
                          marginRight: iconPosition === 'left' ? badge?.icon?.spacingPx : 0,
                          marginLeft: iconPosition === 'right' ? badge?.icon?.spacingPx : 0,
                        }}
                      />
                    );
                    return (
                      <span
                        key={badge?.id || badge?.badgeText || idx}
                        className={`inline-flex w-fit items-center rounded-sm ${selfClass}`}
                        style={{
                          backgroundColor: styles?.backgroundColor,
                          color: styles?.textColor,
                          fontSize: `${!isMobile ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                          fontWeight: styles?.fontWeight,
                          padding: !isMobile ? styles?.paddingMobile : styles?.paddingDesktop,
                          minHeight: `${!isMobile ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                          maxHeight: `${!isMobile ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                          gap: `${!isMobile ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                          borderWidth: `${styles?.borderThickness || 0}px`,
                          borderColor: styles?.borderColor,
                        }}
                      >
                        {iconPosition === 'left' && iconElement}
                        <span>{label}</span>
                        {iconPosition === 'right' && iconElement}
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative  flex-grow overflow-hidden text-ellipsis pb-[15px]  text-[18px] font-medium leading-[22px] md:text-[18px] md:leading-[22px]">
                {/* {product?.name} */}
                {`${product?.name} ${productColour ? `- ${productColour}` : ''}`}
              </div>
            </div>
            {/* </Link> */}
          </div>

          {/* <div
          style={{
            position: 'relative',
            pointerEvents: hasReviews ? 'auto' : 'none',
            display: hasReviews ? 'block' : 'none',
          }}
        >
          <div className="mb-10 ml-3 py-7" style={{ pointerEvents: 'none' }}>
            <TrustBox
              id={trustPilotSkuId}
              trustBoxCss={trustBoxCss}
              isPLP={true}
              showVertical={false}
              onWidgetLoad={setHasReviews}
            />
            <StarImageSrc />
          </div>
        </div> */}

          <div className="flex justify-center">
            <div className="mb-10 flex cursor-pointer items-center justify-center gap-6 py-7">
              {StarImage && <StarImage />}
              {numberOfReviews !== 0 && (
                <span className="text-sm font-normal text-[#1E1E1E]">{numberOfReviews} reviews</span>
              )}
            </div>
          </div>
        </SmartLink>
        {/* Price Section */}
        <div className="mt-4 text-center md:mt-6">
          <div className="flex flex-nowrap items-center justify-center text-[32px] font-bold leading-[32px] text-[#1e1e1e]">
            {/* Need to Fix */}
            {/* <Prices price={selectedVariant?.price} discountedPrice={selectedVariant?.discountedPrice} /> */}
            <PriceMap price={price} discountedPercentage={discountPercentage} mrrpPrice={mrrpPrice} />
          </div>
        </div>

        {/* As per Client requirement disabling the compare button (03.03.2025) */}

        {/* <div className="mt-6 flex hidden  items-center justify-center py-8">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isProductInCompare}
            onChange={() => {
              setOpen(true), handleCompareChange(product);
            }}
            className="text-primary form-checkbox h-20 w-20 rounded-sm"
          />
          <span className="text-sm md:text-base">Compare</span>
        </label>
      </div> */}
        <div className="mt-8 flex min-h-[84px] flex-wrap justify-center gap-6">
          {sortedColorVariants &&
            sortedColorVariants?.map((swatch, index) => (
              <div
                key={index}
                className={`relative inline-block h-[22px] w-[22px] cursor-pointer ${
                  hoveredSwatch === swatch ? '' : ''
                }`}
                onMouseEnter={() => setHoveredSwatch(swatch)}
                onMouseLeave={() => setHoveredSwatch(null)}
                onClick={() => handleColourSelect(swatch, product?.variantCode as any)}
                title={swatch?.colour}
              >
                <CheckedSwatch
                  imgUrl={swatch?.images?.[0]}
                  altText="swatch image"
                  sku={swatch?.sku}
                  isActive={(hoveredSwatch?.sku || selectedSwatch?.sku) === swatch?.sku}
                  colourHexCode={swatch?.colourHexCode ?? ''}
                />
              </div>
            ))}
          {/* {uniqueColorVariants.map((variant, index) => (
                        <div
                            key={index}
                            className={`relative inline-block h-[24px] w-[24px] cursor-pointer pl-2  ${hoveredVariant === variant ? ' ' : ''
                                }`}
                            onMouseEnter={() => setHoveredVariant(variant)}
                            onMouseLeave={() => setHoveredVariant(null)}
                            onClick={() => handleColourSelect(variant, product?.objectId as any)}
                            title={variant?.attributes?.colour}
                        >
                            <CenterImageCropSwatch
                                imgUrl={variant?.images?.[0]}
                                altText="swatch image"
                                sku={variant?.sku}
                                isActive={selectedColour === variant?.attributes?.colour}
                            />
                        </div>
                    ))} */}
        </div>
      </div>
      {/* )} */}
    </>
  );
};

export default ProductListTile;
